import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage, LANGUAGES } from "@/context/LanguageContext";
import { useT } from "@/i18n/useT";

export default function Navbar() {
  const t = useT();
  const { count, setIsOpen } = useCart();
  const { lang, setLang } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV_LINKS = [
    { to: "/shop", label: t.nav.shop, key: "shop" },
    { to: "/story", label: t.nav.ourStory, key: "our-story" },
    { to: "/latvia", label: t.nav.latvia, key: "latvia" },
    { to: "/journal", label: t.nav.journal, key: "journal" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="navbar" className={`sticky top-0 z-40 backdrop-blur-md border-b transition-all duration-500 ${scrolled ? "bg-cream/95 border-walnut/20" : "bg-cream/80 border-transparent"}`}>
      <div className="br-container flex items-center justify-between py-5">
        <Link to="/" data-testid="logo-link" className="font-serif text-2xl tracking-tight text-espresso">BalticRoots</Link>
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.key} to={l.to} data-testid={`nav-link-${l.key}`}
              className={({ isActive }) => `text-xs uppercase tracking-[0.25em] font-sans transition-colors duration-300 ${isActive ? "text-walnut" : "text-espresso hover:text-walnut"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <div className="relative hidden sm:block">
            <button data-testid="lang-toggle" onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 text-xs uppercase tracking-[0.25em] text-espresso hover:text-walnut transition-colors">
              {lang} <ChevronDown className="w-3 h-3" />
            </button>
            {langOpen && (
              <div data-testid="lang-menu" className="absolute right-0 mt-3 bg-cream border border-walnut/20 min-w-[140px] py-2 shadow-sm" onMouseLeave={() => setLangOpen(false)}>
                {LANGUAGES.map((l) => (
                  <button key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-walnut/10 ${lang === l.code ? "text-walnut" : "text-espresso"}`}>
                    {l.code} &nbsp;·&nbsp; {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="cart-button" onClick={() => setIsOpen(true)} className="relative text-espresso hover:text-walnut transition-colors" aria-label="Open cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.4} />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-espresso text-cream text-[10px] w-4 h-4 flex items-center justify-center font-sans">{count}</span>}
          </button>
          <button data-testid="mobile-menu-toggle" className="md:hidden text-espresso" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div data-testid="mobile-nav" className="md:hidden bg-cream border-t border-walnut/20">
          <div className="br-container py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.key} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm uppercase tracking-[0.25em] text-espresso">{l.label}</NavLink>
            ))}
            <div className="flex gap-3 pt-3 border-t border-walnut/20">
              {LANGUAGES.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} className={`text-xs tracking-[0.2em] ${lang === l.code ? "text-walnut font-medium" : "text-espresso/60"}`}>{l.code}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}